/**
 * Profile Validation Utilities
 * Comprehensive validation for all profile-related inputs
 * Following security best practices and user experience guidelines
 */

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export interface ProfileValidationRules {
  firstName: {
    minLength: number;
    maxLength: number;
    required: boolean;
  };
  lastName: {
    minLength: number;
    maxLength: number;
    required: boolean;
  };
  displayName: {
    minLength: number;
    maxLength: number;
    required: boolean;
  };
  bio: {
    maxLength: number;
    required: boolean;
  };
  location: {
    maxLength: number;
    required: boolean;
  };
  website: {
    maxLength: number;
    required: boolean;
  };
  socialMedia: {
    maxLength: number;
    required: boolean;
  };
}

export const PROFILE_VALIDATION_RULES: ProfileValidationRules = {
  firstName: {
    minLength: 1,
    maxLength: 50,
    required: true,
  },
  lastName: {
    minLength: 1,
    maxLength: 50,
    required: true,
  },
  displayName: {
    minLength: 2,
    maxLength: 30,
    required: true,
  },
  bio: {
    maxLength: 500,
    required: false,
  },
  location: {
    maxLength: 100,
    required: false,
  },
  website: {
    maxLength: 200,
    required: false,
  },
  socialMedia: {
    maxLength: 100,
    required: false,
  },
};

/**
 * Sanitize input to prevent XSS attacks
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';

  return (
    input
      // Remove script tags
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // Remove all HTML tags
      .replace(/<[^>]*>/g, '')
      // Remove potentially dangerous characters (but keep dots, slashes, colons for URLs)
      .replace(/[<>'"&]/g, '')
      // Normalize whitespace
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Sanitize URL input - more permissive for URLs
 */
export function sanitizeUrl(input: string): string {
  if (!input) return '';

  return (
    input
      // Remove script tags
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // Remove all HTML tags
      .replace(/<[^>]*>/g, '')
      // Only remove the most dangerous characters
      .replace(/[<>'"]/g, '')
      .trim()
  );
}

/**
 * Validate name fields (first name, last name)
 */
export function validateName(
  name: string,
  fieldName: string
): ValidationResult {
  const sanitized = sanitizeInput(name);

  if (!sanitized) {
    return {
      isValid: false,
      error: `${fieldName} is required`,
    };
  }

  if (sanitized.length < PROFILE_VALIDATION_RULES.firstName.minLength) {
    return {
      isValid: false,
      error: `${fieldName} must be at least ${PROFILE_VALIDATION_RULES.firstName.minLength} character`,
    };
  }

  if (sanitized.length > PROFILE_VALIDATION_RULES.firstName.maxLength) {
    return {
      isValid: false,
      error: `${fieldName} must be less than ${PROFILE_VALIDATION_RULES.firstName.maxLength} characters`,
    };
  }

  // Only allow letters, spaces, hyphens, and apostrophes
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  if (!nameRegex.test(sanitized)) {
    return {
      isValid: false,
      error: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`,
    };
  }

  return { isValid: true };
}

/**
 * Validate display name
 */
export function validateDisplayName(displayName: string): ValidationResult {
  const sanitized = sanitizeInput(displayName);

  if (!sanitized) {
    return {
      isValid: false,
      error: 'Display name is required',
    };
  }

  if (sanitized.length < PROFILE_VALIDATION_RULES.displayName.minLength) {
    return {
      isValid: false,
      error: `Display name must be at least ${PROFILE_VALIDATION_RULES.displayName.minLength} characters`,
    };
  }

  if (sanitized.length > PROFILE_VALIDATION_RULES.displayName.maxLength) {
    return {
      isValid: false,
      error: `Display name must be less than ${PROFILE_VALIDATION_RULES.displayName.maxLength} characters`,
    };
  }

  // Allow letters, numbers, spaces, and common symbols
  const displayNameRegex = /^[a-zA-Z0-9\s\-_.]+$/;
  if (!displayNameRegex.test(sanitized)) {
    return {
      isValid: false,
      error:
        'Display name can only contain letters, numbers, spaces, hyphens, underscores, and periods',
    };
  }

  return { isValid: true };
}

/**
 * Validate bio
 */
export function validateBio(bio: string): ValidationResult {
  const sanitized = sanitizeInput(bio);

  if (sanitized.length > PROFILE_VALIDATION_RULES.bio.maxLength) {
    return {
      isValid: false,
      error: `Bio must be less than ${PROFILE_VALIDATION_RULES.bio.maxLength} characters`,
    };
  }

  return { isValid: true };
}

/**
 * Validate location
 */
export function validateLocation(location: string): ValidationResult {
  const sanitized = sanitizeInput(location);

  if (sanitized.length > PROFILE_VALIDATION_RULES.location.maxLength) {
    return {
      isValid: false,
      error: `Location must be less than ${PROFILE_VALIDATION_RULES.location.maxLength} characters`,
    };
  }

  return { isValid: true };
}

/**
 * Validate website URL
 */
export function validateWebsite(url: string): ValidationResult {
  if (!url.trim()) {
    return { isValid: true }; // Optional field
  }

  const sanitized = sanitizeUrl(url);

  if (sanitized.length > PROFILE_VALIDATION_RULES.website.maxLength) {
    return {
      isValid: false,
      error: `Website URL must be less than ${PROFILE_VALIDATION_RULES.website.maxLength} characters`,
    };
  }

  try {
    const urlObj = new URL(
      sanitized.startsWith('http') ? sanitized : `https://${sanitized}`
    );

    // Only allow http/https protocols
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return {
        isValid: false,
        error: 'Website URL must use http or https protocol',
      };
    }

    // Basic domain validation
    if (!urlObj.hostname || urlObj.hostname.length < 3) {
      return {
        isValid: false,
        error: 'Please enter a valid website URL',
      };
    }

    return { isValid: true };
  } catch {
    return {
      isValid: false,
      error: 'Please enter a valid website URL',
    };
  }
}

/**
 * Validate Instagram username/URL
 */
export function validateInstagram(input: string): ValidationResult {
  if (!input.trim()) {
    return { isValid: true }; // Optional field
  }

  // Clean the URL to extract username - don't sanitize yet
  const cleanInput = input
    .replace(/^https?:\/\/(www\.)?instagram\.com\/@?/, '')
    .replace(/\/$/, '')
    .replace(/^@/, '')
    .trim();

  if (cleanInput.length > PROFILE_VALIDATION_RULES.socialMedia.maxLength) {
    return {
      isValid: false,
      error: `Instagram username must be less than ${PROFILE_VALIDATION_RULES.socialMedia.maxLength} characters`,
    };
  }

  // Instagram username validation: 1-30 characters, letters, numbers, periods, underscores
  const instagramRegex = /^[a-zA-Z0-9._]{1,30}$/;
  if (!instagramRegex.test(cleanInput)) {
    return {
      isValid: false,
      error:
        'Instagram username can only contain letters, numbers, periods, and underscores (1-30 characters)',
    };
  }

  // Prevent consecutive periods
  if (cleanInput.includes('..')) {
    return {
      isValid: false,
      error: 'Instagram username cannot contain consecutive periods',
    };
  }

  return { isValid: true };
}

/**
 * Validate Twitter username/URL
 */
export function validateTwitter(input: string): ValidationResult {
  if (!input.trim()) {
    return { isValid: true }; // Optional field
  }

  // Clean the URL to extract username - don't sanitize yet
  const cleanInput = input
    .replace(/^https?:\/\/(www\.)?twitter\.com\/@?/, '')
    .replace(/\/$/, '')
    .replace(/^@/, '')
    .trim();

  if (cleanInput.length > PROFILE_VALIDATION_RULES.socialMedia.maxLength) {
    return {
      isValid: false,
      error: `Twitter username must be less than ${PROFILE_VALIDATION_RULES.socialMedia.maxLength} characters`,
    };
  }

  // Twitter username validation: 1-15 characters, letters, numbers, underscores
  const twitterRegex = /^[a-zA-Z0-9_]{1,15}$/;
  if (!twitterRegex.test(cleanInput)) {
    return {
      isValid: false,
      error:
        'Twitter username can only contain letters, numbers, and underscores (1-15 characters)',
    };
  }

  return { isValid: true };
}

/**
 * Validate YouTube channel URL
 */
export function validateYouTube(input: string): ValidationResult {
  if (!input.trim()) {
    return { isValid: true }; // Optional field
  }

  const sanitized = sanitizeUrl(input);

  if (sanitized.length > PROFILE_VALIDATION_RULES.socialMedia.maxLength) {
    return {
      isValid: false,
      error: `YouTube URL must be less than ${PROFILE_VALIDATION_RULES.socialMedia.maxLength} characters`,
    };
  }

  // Check if it's a valid YouTube URL or just a channel name
  const youtubeRegex =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/(channel\/|user\/|c\/|@)|youtu\.be\/)/i;

  // Allow just channel names/handles without full URL
  const channelNameRegex = /^@?[a-zA-Z0-9_-]{3,30}$/;

  if (!youtubeRegex.test(sanitized) && !channelNameRegex.test(sanitized)) {
    return {
      isValid: false,
      error: 'Please enter a valid YouTube channel URL or handle',
    };
  }

  return { isValid: true };
}

/**
 * Validate TikTok username/URL
 */
export function validateTikTok(input: string): ValidationResult {
  if (!input.trim()) {
    return { isValid: true }; // Optional field
  }

  // Clean the URL to extract username - don't sanitize yet
  const cleanInput = input
    .replace(/^https?:\/\/(www\.)?tiktok\.com\/@?/, '')
    .replace(/\/$/, '')
    .replace(/^@/, '')
    .trim();

  if (cleanInput.length > PROFILE_VALIDATION_RULES.socialMedia.maxLength) {
    return {
      isValid: false,
      error: `TikTok username must be less than ${PROFILE_VALIDATION_RULES.socialMedia.maxLength} characters`,
    };
  }

  // TikTok username validation: 1-24 characters, letters, numbers, periods, underscores
  const tiktokRegex = /^[a-zA-Z0-9._]{1,24}$/;
  if (!tiktokRegex.test(cleanInput)) {
    return {
      isValid: false,
      error:
        'TikTok username can only contain letters, numbers, periods, and underscores (1-24 characters)',
    };
  }

  return { isValid: true };
}

/**
 * Validate password strength
 */
export function validatePassword(password: string): ValidationResult {
  if (!password) {
    return {
      isValid: false,
      error: 'Password is required',
    };
  }

  if (password.length < 8) {
    return {
      isValid: false,
      error: 'Password must be at least 8 characters long',
    };
  }

  if (password.length > 128) {
    return {
      isValid: false,
      error: 'Password must be less than 128 characters long',
    };
  }

  // Check for at least one lowercase letter
  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one lowercase letter',
    };
  }

  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one uppercase letter',
    };
  }

  // Check for at least one number
  if (!/\d/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one number',
    };
  }

  // Check for at least one special character
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    return {
      isValid: false,
      error: 'Password must contain at least one special character',
    };
  }

  return { isValid: true };
}

/**
 * Validate all profile fields at once
 */
export function validateProfileForm(formData: {
  first_name: string;
  last_name: string;
  display_name: string;
  bio: string;
  location: string;
  website: string;
  instagram: string;
  twitter: string;
  youtube: string;
  tiktok: string;
}): { [key: string]: string } {
  const errors: { [key: string]: string } = {};

  const firstNameResult = validateName(formData.first_name, 'First name');
  if (!firstNameResult.isValid) {
    errors.first_name = firstNameResult.error!;
  }

  const lastNameResult = validateName(formData.last_name, 'Last name');
  if (!lastNameResult.isValid) {
    errors.last_name = lastNameResult.error!;
  }

  const displayNameResult = validateDisplayName(formData.display_name);
  if (!displayNameResult.isValid) {
    errors.display_name = displayNameResult.error!;
  }

  const bioResult = validateBio(formData.bio);
  if (!bioResult.isValid) {
    errors.bio = bioResult.error!;
  }

  const locationResult = validateLocation(formData.location);
  if (!locationResult.isValid) {
    errors.location = locationResult.error!;
  }

  const websiteResult = validateWebsite(formData.website);
  if (!websiteResult.isValid) {
    errors.website = websiteResult.error!;
  }

  const instagramResult = validateInstagram(formData.instagram);
  if (!instagramResult.isValid) {
    errors.instagram = instagramResult.error!;
  }

  const twitterResult = validateTwitter(formData.twitter);
  if (!twitterResult.isValid) {
    errors.twitter = twitterResult.error!;
  }

  const youtubeResult = validateYouTube(formData.youtube);
  if (!youtubeResult.isValid) {
    errors.youtube = youtubeResult.error!;
  }

  const tiktokResult = validateTikTok(formData.tiktok);
  if (!tiktokResult.isValid) {
    errors.tiktok = tiktokResult.error!;
  }

  return errors;
}
